import React, { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm, SubmitHandler } from 'react-hook-form'
import * as Yup from 'yup'

import { SendCodeEmailRequestDTO } from '@/core/api/dtos/request/zone/sendCodeEmail'
import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface SendCodeEmailZoneModalProps {
	visible: boolean
	zone: Zone | null
	onClose: () => void
	onConfirm: (data: SendCodeEmailRequestDTO) => void
	isSending: boolean
}

// Local form values type with required email
interface SendCodeEmailFormValues {
	email: string
}

const SendCodeEmailZoneModal: React.FC<SendCodeEmailZoneModalProps> = ({
	visible,
	onClose,
	onConfirm,
	isSending,
}) => {
	const { t } = useTranslation('common')

	// Default values for the form
	const defaultValues: SendCodeEmailFormValues = {
		email: '',
	}

	// Create validation schema using Yup
	const validationSchema = Yup.object().shape({
		email: Yup.string()
			.email(t('validation.email'))
			.required(t('validation.required'))
			.default(defaultValues.email),
	})

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<SendCodeEmailFormValues>({
		defaultValues,
		resolver: yupResolver(validationSchema),
	})

	// Reset form when modal closes
	useEffect(() => {
		if (!visible) {
			reset(defaultValues)
		}
	}, [visible, reset])

	const onSubmit: SubmitHandler<SendCodeEmailFormValues> = (data) => {
		// Convert from form values to DTO
		const emailData: SendCodeEmailRequestDTO = {
			email: data.email,
		}
		onConfirm(emailData)
	}

	const handleOpenChange = (open: boolean) => {
		if (!open && !isSending) {
			onClose()
		}
	}

	return (
		<Dialog open={visible} onOpenChange={handleOpenChange}>
			<DialogContent
				className="sm:max-w-[500px] w-[90%]"
				showCloseButton={!isSending}
			>
				<div className="flex flex-col items-center text-center gap-4 py-4">
					<DialogHeader className="text-center">
						<DialogTitle>{t('entities.zone.email_code.title')}</DialogTitle>
					</DialogHeader>

					<div className="!mb-0 text-base text-foreground leading-[20px] tracking-[0.25px]">
						{t('entities.zone.email_code.description')}
					</div>

					<form
						id="send-code-email-form"
						onSubmit={handleSubmit(onSubmit)}
						className="w-full"
					>
						<div className="flex flex-col gap-4 w-full max-w-[300px] mt-6 mx-auto">
							<div className="w-full text-left flex flex-col gap-2">
								<Label
									htmlFor="email"
									className="inline-flex items-center gap-1 text-sm text-foreground"
								>
									{t('entities.zone.email_code.form.email.label')}
									<span className="text-primary">*</span>
								</Label>
								<Controller
									name="email"
									control={control}
									render={({ field }) => (
										<>
											<Input
												{...field}
												id="email"
												placeholder={t(
													'entities.zone.email_code.form.email.placeholder'
												)}
												disabled={isSending}
											/>
											{errors.email && (
												<ErrorMessage error={errors.email.message || ''} />
											)}
										</>
									)}
								/>
							</div>
						</div>
					</form>

					<DialogFooter className="w-full gap-3">
						<Button variant="outline" onClick={onClose} disabled={isSending}>
							{t('actions.cancel')}
						</Button>
						<Button
							variant="default"
							onClick={handleSubmit(onSubmit)}
							disabled={isSending}
							form="send-code-email-form"
							type="submit"
							className="rounded-full px-6 bg-white text-black hover:bg-gray-100"
						>
							{t('entities.zone.email_code.action')}
						</Button>
					</DialogFooter>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default SendCodeEmailZoneModal
